import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';
import { translateLocation } from '../../utils/translationUtils';
import Modal from '../../components/Modal';
import './Payments.css';

const AdminPayments = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext() || {};
  const { data, addPayment, registerPayment, updatePayment, deletePayment } = useData();
  const paymentMethodOptions = ['bank transfer', 'cash', 'pin'];

  const [selectedStudentFilter, setSelectedStudentFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
  const [selectedEmailFilter, setSelectedEmailFilter] = useState('');
  const [selectedNameFilter, setSelectedNameFilter] = useState('');
  const [selectedPeriodFrom, setSelectedPeriodFrom] = useState('');
  const [selectedPeriodTo, setSelectedPeriodTo] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedPaymentIds, setExpandedPaymentIds] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [recordingPayment, setRecordingPayment] = useState(null);
  const [recordForm, setRecordForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: 'bank transfer',
    notes: ''
  });
  const [newPayment, setNewPayment] = useState({
    studentId: '',
    totalAmount: '',
    description: '',
    paymentType: 'one-time',
    schoolYear: '',
    dueDate: '',
    paymentMethod: 'bank transfer',
    notes: ''
  });

  const normalizedLocationFilter = useMemo(() => {
    if (!selectedLocation || selectedLocation === 'all') return '';
    return selectedLocation.toLowerCase();
  }, [selectedLocation]);

  const students = useMemo(() => {
    let list = data.students || [];
    if (user.role === 'manager') {
      list = list.filter((s) => (s.location || '').toLowerCase() === (user.location || '').toLowerCase());
    }
    if (normalizedLocationFilter) {
      list = list.filter((s) => (s.location || '').toLowerCase() === normalizedLocationFilter);
    }
    return list;
  }, [data.students, normalizedLocationFilter, user.location, user.role]);

  const courses = useMemo(() => {
    let list = data.courses || [];
    if (user.role === 'manager') {
      list = list.filter((c) => (c.location || '').toLowerCase() === (user.location || '').toLowerCase());
    }
    if (normalizedLocationFilter) {
      list = list.filter((c) => (c.location || '').toLowerCase() === normalizedLocationFilter);
    }
    return list;
  }, [data.courses, normalizedLocationFilter, user.location, user.role]);

  const studentGroups = useMemo(() => {
    const grouped = new Map();
    students.forEach((student) => {
      const locationKey = student.location || 'Onbekend';
      if (!grouped.has(locationKey)) {
        grouped.set(locationKey, []);
      }
      grouped.get(locationKey).push(student);
    });

    return Array.from(grouped.entries()).map(([label, items]) => ({
      label,
      items: [...items].sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'nl'))
    }));
  }, [students]);

  const courseGroups = useMemo(() => {
    const grouped = new Map();
    courses.forEach((course) => {
      const locationKey = course.location || 'Onbekend';
      if (!grouped.has(locationKey)) {
        grouped.set(locationKey, []);
      }
      grouped.get(locationKey).push(course);
    });

    return Array.from(grouped.entries()).map(([label, items]) => ({
      label,
      items: [...items].sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'nl'))
    }));
  }, [courses]);

  const filteredPayments = useMemo(() => {
    let list = data.payments || [];

    if (user.role === 'manager') {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        const managerLocation = (user.location || '').toLowerCase();
        return studentLocation === managerLocation || courseLocation === managerLocation;
      });
    }

    if (normalizedLocationFilter) {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        return studentLocation === normalizedLocationFilter || courseLocation === normalizedLocationFilter;
      });
    }

    if (selectedStudentFilter) {
      list = list.filter((p) => String(p.studentId || p.student?.id) === String(selectedStudentFilter));
    }

    if (selectedCourseFilter) {
      list = list.filter((p) => String(p.courseId || p.course?.id) === String(selectedCourseFilter));
    }

    if (selectedStatusFilter) {
      list = list.filter((p) => p.status === selectedStatusFilter);
    }

    if (selectedLocationFilter) {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        return studentLocation === selectedLocationFilter || courseLocation === selectedLocationFilter;
      });
    }

    if (selectedNameFilter) {
      const needle = selectedNameFilter.toLowerCase();
      list = list.filter((p) => String(p.student?.name || '').toLowerCase().includes(needle));
    }

    if (selectedEmailFilter) {
      const needle = selectedEmailFilter.toLowerCase();
      list = list.filter((p) => String(p.student?.email || '').toLowerCase().includes(needle));
    }

    if (selectedPeriodFrom) {
      const from = new Date(`${selectedPeriodFrom}T00:00:00`);
      list = list.filter((p) => {
        const paymentDate = p.paymentDate || p.dueDate || p.createdAt;
        if (!paymentDate) return false;
        return new Date(paymentDate) >= from;
      });
    }

    if (selectedPeriodTo) {
      const to = new Date(`${selectedPeriodTo}T23:59:59`);
      list = list.filter((p) => {
        const paymentDate = p.paymentDate || p.dueDate || p.createdAt;
        if (!paymentDate) return false;
        return new Date(paymentDate) <= to;
      });
    }

    if (minAmountFilter !== '') {
      const min = Number(minAmountFilter);
      list = list.filter((p) => Number((p.totalAmount ?? p.amount) || 0) >= min);
    }

    if (maxAmountFilter !== '') {
      const max = Number(maxAmountFilter);
      list = list.filter((p) => Number((p.totalAmount ?? p.amount) || 0) <= max);
    }

    return list;
  }, [
    data.payments,
    normalizedLocationFilter,
    selectedStudentFilter,
    selectedCourseFilter,
    selectedStatusFilter,
    selectedLocationFilter,
    selectedNameFilter,
    selectedEmailFilter,
    selectedPeriodFrom,
    selectedPeriodTo,
    minAmountFilter,
    maxAmountFilter,
    user.location,
    user.role
  ]);

  const totals = useMemo(() => {
    return filteredPayments.reduce((acc, p) => {
      const amount = Number(p.amount || 0);
      const totalAmount = Number((p.totalAmount ?? amount) || 0);
      const paidAmount = Number(p.paidAmount || 0);
      const remainingAmount = Math.max(0, totalAmount - paidAmount);
      acc.total += totalAmount;
      acc.paid += paidAmount;
      acc.pending += remainingAmount;
      return acc;
    }, { total: 0, paid: 0, pending: 0 });
  }, [filteredPayments]);

  const getRemainingAmount = (payment) => {
    const total = Number((payment.totalAmount ?? payment.amount) || 0);
    const paid = Number(payment.paidAmount || 0);
    return Math.max(0, total - paid);
  };

  const formatCurrency = (amount) => {
    const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'SRD' }).format(Number(amount || 0));
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.studentId || !newPayment.totalAmount) return;

    const parsedTotal = Number(newPayment.totalAmount);
    if (Number.isNaN(parsedTotal) || parsedTotal <= 0) return;

    await addPayment({
      studentId: newPayment.studentId,
      amount: parsedTotal,
      totalAmount: parsedTotal,
      description: newPayment.description || undefined,
      paymentType: newPayment.paymentType || 'one-time',
      schoolYear: newPayment.schoolYear || undefined,
      dueDate: newPayment.dueDate || undefined,
      paymentMethod: newPayment.paymentMethod || undefined,
      notes: newPayment.notes || undefined
    });

    setNewPayment({
      studentId: '',
      totalAmount: '',
      description: '',
      paymentType: 'one-time',
      schoolYear: '',
      dueDate: '',
      paymentMethod: 'bank transfer',
      notes: ''
    });
    setShowCreateForm(false);
  };

  const openRecordPaymentModal = (payment) => {
    const remaining = getRemainingAmount(payment);
    if (remaining <= 0) return;

    setRecordingPayment(payment);
    setRecordForm({
      amount: String(remaining),
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentMethod: payment.paymentMethod || 'bank transfer',
      notes: ''
    });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!recordingPayment?.id) return;

    const amount = Number(recordForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      window.alert('Voer een geldig aflossingsbedrag in.');
      return;
    }

    const paymentDateIso = recordForm.paymentDate
      ? `${recordForm.paymentDate}T00:00:00-03:00`
      : new Date().toISOString();

    const remaining = getRemainingAmount(recordingPayment);
    if (amount > remaining) {
      window.alert(`Aflossing kan niet hoger zijn dan openstaand bedrag (${formatCurrency(remaining)}).`);
      return;
    }

    await registerPayment(
      recordingPayment.id,
      amount,
      (recordForm.paymentMethod || 'bank transfer').trim(),
      recordForm.notes || `Aflossing geregistreerd: ${amount}`,
      paymentDateIso
    );

    setRecordingPayment(null);
    setRecordForm({
      amount: '',
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentMethod: 'bank transfer',
      notes: ''
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingPayment?.id) return;

    await updatePayment(editingPayment.id, {
      paymentMethod: editingPayment.paymentMethod,
      description: editingPayment.description,
      paymentType: editingPayment.paymentType,
      schoolYear: editingPayment.schoolYear,
      totalAmount: Number((editingPayment.totalAmount ?? editingPayment.amount) || 0),
      notes: editingPayment.notes,
      status: editingPayment.status,
      installments: (editingPayment.installmentsDraft || []).map((entry) => ({
        amount: Number(entry.amount || 0),
        paymentDate: entry.paymentDate,
        paymentMethod: entry.paymentMethod,
        notes: entry.notes
      }))
    });

    setEditingPayment(null);
  };

  const handleDeletePayment = async (payment) => {
    if (user.role !== 'admin') return;
    if (!payment?.id) return;

    const confirmed = window.confirm(`Weet je zeker dat je deze betaling wilt verwijderen voor ${payment.student?.name || 'deze student'}?`);
    if (!confirmed) return;

    try {
      await deletePayment(payment.id);
    } catch (error) {
      window.alert(error?.message || 'Betaling verwijderen is mislukt.');
    }
  };

  const handleExportPDF = () => {
    const exportData = filteredPayments.map((p) => ({
      [t('payment.exportHeaders.student')]: p.student?.name || '-',
      [t('payment.exportHeaders.location')]: p.student?.location || '-',
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToPDF(exportData, 'NLBA_Admin_Payments', t('admin.payments'));
  };

  const handleExportExcel = () => {
    const exportData = filteredPayments.map((p) => ({
      [t('payment.exportHeaders.student')]: p.student?.name || '-',
      [t('payment.exportHeaders.location')]: p.student?.location || '-',
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToExcel(exportData, 'NLBA_Admin_Payments', t('payment.payments'), t('admin.payments'));
  };

  const toggleExpandedPayment = (paymentId) => {
    setExpandedPaymentIds((prev) => ({
      ...prev,
      [paymentId]: !prev[paymentId]
    }));
  };

  const resetFilters = () => {
    setSelectedStudentFilter('');
    setSelectedStatusFilter('');
    setSelectedCourseFilter('');
    setSelectedLocationFilter('');
    setSelectedNameFilter('');
    setSelectedEmailFilter('');
    setSelectedPeriodFrom('');
    setSelectedPeriodTo('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  return (
    <div className="admin-payments-page">
      <div className="page-header">
        <h1>{t('admin.payments')}</h1>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleExportPDF} disabled={filteredPayments.length === 0}>
            {t('report.exportPDF')}
          </button>
          <button className="btn-secondary" onClick={handleExportExcel} disabled={filteredPayments.length === 0}>
            {t('report.exportExcel')}
          </button>
          <button className="btn-primary" onClick={() => setShowCreateForm((prev) => !prev)}>
            {t('payment.addPayment')}
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="form-group">
          <label>{t('report.selectStudent')}</label>
          <select className="clean-select" value={selectedStudentFilter} onChange={(e) => setSelectedStudentFilter(e.target.value)}>
            <option value="">{t('common.allData')}...</option>
            {studentGroups.map((group) => (
              <optgroup key={group.label} label={translateLocation(t, String(group.label).toLowerCase()) || group.label}>
                {group.items.map((student) => (
                  <option key={student.id} value={student.id}>{student.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t('common.status')}</label>
          <select className="clean-select" value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)}>
            <option value="">{t('common.allData')}...</option>
            <option value="pending">{t('payment.status.pending')}</option>
            <option value="partial">{t('payment.status.partial', 'Deels betaald')}</option>
            <option value="paid">{t('payment.status.paid')}</option>
            <option value="overdue">{t('payment.status.overdue')}</option>
            <option value="cancelled">{t('payment.status.cancelled')}</option>
          </select>
        </div>
        <div className="form-group compact-actions">
          <label>Extra filters</label>
          <div className="form-actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn-secondary" onClick={() => setShowAdvancedFilters((prev) => !prev)}>
              {showAdvancedFilters ? 'Minder filters' : 'Meer filters'}
            </button>
            <button type="button" className="btn-secondary" onClick={resetFilters}>Reset</button>
          </div>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="filter-section advanced-filter-section">
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label>{t('course.title')}</label>
              <select className="clean-select" value={selectedCourseFilter} onChange={(e) => setSelectedCourseFilter(e.target.value)}>
                <option value="">{t('common.allData')}...</option>
                {courseGroups.map((group) => (
                  <optgroup key={group.label} label={translateLocation(t, String(group.label).toLowerCase()) || group.label}>
                    {group.items.map((course) => (
                      <option key={course.id} value={course.id}>{course.code} - {course.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('location.title')}</label>
              <select className="clean-select" value={selectedLocationFilter} onChange={(e) => setSelectedLocationFilter(e.target.value)}>
                <option value="">{t('common.allData')}...</option>
                {(data.locations || []).map((l) => (
                  <option key={l.id} value={l.id}>{translateLocation(t, l.id)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Naam</label>
              <input className="clean-input" type="text" value={selectedNameFilter} onChange={(e) => setSelectedNameFilter(e.target.value)} placeholder="Zoek op naam" />
            </div>
            <div className="form-group">
              <label>E-mail</label>
              <input className="clean-input" type="text" value={selectedEmailFilter} onChange={(e) => setSelectedEmailFilter(e.target.value)} placeholder="Zoek op e-mail" />
            </div>
            <div className="form-group">
              <label>Periode van</label>
              <input className="clean-input" type="date" value={selectedPeriodFrom} onChange={(e) => setSelectedPeriodFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Periode tot</label>
              <input className="clean-input" type="date" value={selectedPeriodTo} onChange={(e) => setSelectedPeriodTo(e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('payment.amount')} (min)</label>
              <input className="clean-input" type="number" min="0" step="0.01" value={minAmountFilter} onChange={(e) => setMinAmountFilter(e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('payment.amount')} (max)</label>
              <input className="clean-input" type="number" min="0" step="0.01" value={maxAmountFilter} onChange={(e) => setMaxAmountFilter(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} title={t('payment.addPayment')} position="right" size="lg">
          <form onSubmit={handleCreatePayment}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('enrollment.student')}</label>
                <select
                  value={newPayment.studentId}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, studentId: e.target.value }))}
                  required
                >
                  <option value="">{t('common.select')}...</option>
                  {studentGroups.map((group) => (
                    <optgroup key={group.label} label={translateLocation(t, String(group.label).toLowerCase()) || group.label}>
                      {group.items.map((student) => (
                        <option key={student.id} value={student.id}>{student.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.type')}</label>
                <select
                  value={newPayment.paymentType}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, paymentType: e.target.value }))}
                >
                  <option value="one-time">Volledig</option>
                  <option value="school-year">Aflossing</option>
                  <option value="enrollment-fee">{t('payment.enrollmentFee')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('studyYear.title')}</label>
                <input
                  type="text"
                  value={newPayment.schoolYear}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, schoolYear: e.target.value }))}
                  placeholder="2025-2026"
                />
              </div>
              <div className="form-group">
                <label>{t('payment.title')}</label>
                <input
                  type="text"
                  value={newPayment.description}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Te betalen bedrag schooljaar</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPayment.totalAmount}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, totalAmount: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('payment.dueDate')}</label>
                <input
                  type="date"
                  value={newPayment.dueDate}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.paymentMethod')}</label>
                <select
                  value={newPayment.paymentMethod}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {paymentMethodOptions.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('course.description')}</label>
                <input
                  type="text"
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowCreateForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
      </Modal>

      <div className="payments-summary">
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalStudents')}</span>
          <span className="summary-value">{filteredPayments.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalDue')}</span>
          <span className="summary-value">{formatCurrency(totals.total)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalPaid')}</span>
          <span className="summary-value paid">{formatCurrency(totals.paid)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.pending')}</span>
          <span className="summary-value pending">{formatCurrency(totals.pending)}</span>
        </div>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>{t('enrollment.student')}</th>
              <th>{t('course.title')}</th>
              <th>Totaal</th>
              <th>Betaald</th>
              <th>Resterend</th>
              <th>Aflossingen</th>
              <th>{t('payment.paymentMethod')}</th>
              <th>{t('payment.dueDate')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map((payment) => (
              <tr key={payment.id}>
                <td>
                  <div className="student-info">
                    <span className="student-name">{payment.student?.name || '-'}</span>
                    <span className="student-location">{translateLocation(t, (payment.student?.location || '').toLowerCase())}</span>
                  </div>
                </td>
                <td>{payment.course?.name || '-'}</td>
                <td>{formatCurrency(payment.totalAmount ?? payment.amount)}</td>
                <td>{formatCurrency(payment.paidAmount || 0)}</td>
                <td>{formatCurrency(getRemainingAmount(payment))}</td>
                <td>
                  <details className="installments-dropdown">
                    <summary>Aflossingen ({(payment.payments || []).length})</summary>
                    {(payment.payments || []).length === 0 ? (
                      <p className="installments-empty">Nog geen aflossingen</p>
                    ) : (
                      <ul className="installments-list">
                        {(payment.payments || []).map((entry, idx) => (
                          <li key={entry.id || idx}>
                            <span>{formatCurrency(entry.amount)}</span>
                            <span>{entry.paymentDate ? new Date(entry.paymentDate).toLocaleDateString('nl-NL') : '-'}</span>
                            <span>{entry.paymentMethod || '-'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </details>
                </td>
                <td>{payment.paymentMethod || '-'}</td>
                <td>{payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}</td>
                <td>
                  <span className={`status-badge status-${payment.status}`}>
                    {t(`payment.status.${payment.status}`, payment.status)}
                  </span>
                </td>
                <td>
                  <div className="payment-row-actions">
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => toggleExpandedPayment(payment.id)}
                    >
                      {expandedPaymentIds[payment.id] ? 'Verberg aflossingen' : `Aflossingen (${(payment.payments || []).length})`}
                    </button>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => setEditingPayment({
                        ...payment,
                        installmentsDraft: (payment.payments || []).map((entry) => ({
                          id: entry.id,
                          amount: String(entry.amount ?? ''),
                          paymentDate: entry.paymentDate ? new Date(entry.paymentDate).toISOString().slice(0, 10) : '',
                          paymentMethod: entry.paymentMethod || payment.paymentMethod || 'bank transfer',
                          notes: entry.notes || ''
                        }))
                      })}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => openRecordPaymentModal(payment)}
                      disabled={getRemainingAmount(payment) <= 0}
                    >
                      Aflossing boeken
                    </button>
                    {user.role === 'admin' && (
                      <button
                        type="button"
                        className="btn-small btn-danger"
                        onClick={() => handleDeletePayment(payment)}
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </div>

                  {expandedPaymentIds[payment.id] && (
                    <div className="payment-row-subtable">
                      <div className="subtable-header">Aflossingen</div>
                      {(payment.payments || []).length === 0 ? (
                        <p className="installments-empty">Nog geen aflossingen</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Bedrag</th>
                              <th>Datum</th>
                              <th>Methode</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(payment.payments || []).map((entry, idx) => (
                              <tr key={entry.id || idx}>
                                <td className="subtable-amount">{formatCurrency(entry.amount)}</td>
                                <td>{entry.paymentDate ? new Date(entry.paymentDate).toLocaleDateString('nl-NL') : '-'}</td>
                                <td>{entry.paymentMethod || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!editingPayment} onClose={() => setEditingPayment(null)} title={t('common.edit')} size="lg" position="right">
        {editingPayment && (
          <form onSubmit={handleSaveEdit}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.paymentMethod')}</label>
                <select
                  value={editingPayment.paymentMethod || ''}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {paymentMethodOptions.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('common.status')}</label>
                <select
                  value={editingPayment.status || 'pending'}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pending">{t('payment.status.pending')}</option>
                  <option value="partial">{t('payment.status.partial', 'Deels betaald')}</option>
                  <option value="paid">{t('payment.status.paid')}</option>
                  <option value="overdue">{t('payment.status.overdue')}</option>
                  <option value="cancelled">{t('payment.status.cancelled')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{t('payment.title')}</label>
              <input
                type="text"
                value={editingPayment.description || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.type')}</label>
                <select
                  value={editingPayment.paymentType || 'one-time'}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, paymentType: e.target.value }))}
                >
                  <option value="one-time">Volledig</option>
                  <option value="school-year">Aflossing</option>
                  <option value="enrollment-fee">{t('payment.enrollmentFee')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>Totaalbedrag</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingPayment.totalAmount ?? editingPayment.amount ?? ''}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, totalAmount: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('studyYear.title')}</label>
              <input
                type="text"
                value={editingPayment.schoolYear || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, schoolYear: e.target.value }))}
                placeholder="2025-2026"
              />
            </div>
            <div className="form-group">
              <label>{t('course.description')}</label>
              <input
                type="text"
                value={editingPayment.notes || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Aflossingen</label>
              <div className="installments-editor">
                {(editingPayment.installmentsDraft || []).map((entry, idx) => (
                  <div key={entry.id || idx} className="installment-editor-row">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={entry.amount}
                      onChange={(e) => setEditingPayment((prev) => {
                        const next = [...(prev.installmentsDraft || [])];
                        next[idx] = { ...next[idx], amount: e.target.value };
                        return { ...prev, installmentsDraft: next };
                      })}
                      placeholder="Bedrag"
                    />
                    <input
                      type="date"
                      value={entry.paymentDate}
                      onChange={(e) => setEditingPayment((prev) => {
                        const next = [...(prev.installmentsDraft || [])];
                        next[idx] = { ...next[idx], paymentDate: e.target.value };
                        return { ...prev, installmentsDraft: next };
                      })}
                    />
                    <select
                      value={entry.paymentMethod}
                      onChange={(e) => setEditingPayment((prev) => {
                        const next = [...(prev.installmentsDraft || [])];
                        next[idx] = { ...next[idx], paymentMethod: e.target.value };
                        return { ...prev, installmentsDraft: next };
                      })}
                    >
                      {paymentMethodOptions.map((method) => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn-small btn-danger"
                      onClick={() => setEditingPayment((prev) => ({
                        ...prev,
                        installmentsDraft: (prev.installmentsDraft || []).filter((_, draftIdx) => draftIdx !== idx)
                      }))}
                    >
                      Verwijderen
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingPayment((prev) => ({
                    ...prev,
                    installmentsDraft: [
                      ...(prev.installmentsDraft || []),
                      {
                        amount: '',
                        paymentDate: new Date().toISOString().slice(0, 10),
                        paymentMethod: prev.paymentMethod || 'bank transfer',
                        notes: ''
                      }
                    ]
                  }))}
                >
                  + Aflossing toevoegen
                </button>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setEditingPayment(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!recordingPayment}
        onClose={() => setRecordingPayment(null)}
        title="Aflossing boeken"
        size="sm"
        position="right"
      >
        {recordingPayment && (
          <form onSubmit={handleRecordPayment}>
            <p style={{ marginTop: 0 }}>
              Openstaand bedrag: <strong>{formatCurrency(getRemainingAmount(recordingPayment))}</strong>
            </p>
            <div className="form-group">
              <label>Bedrag</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={recordForm.amount}
                onChange={(e) => setRecordForm((prev) => ({ ...prev, amount: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Datum afboeking</label>
              <input
                type="date"
                value={recordForm.paymentDate}
                onChange={(e) => setRecordForm((prev) => ({ ...prev, paymentDate: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('payment.paymentMethod')}</label>
              <select
                value={recordForm.paymentMethod}
                onChange={(e) => setRecordForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
              >
                {paymentMethodOptions.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Notitie</label>
              <input
                type="text"
                value={recordForm.notes}
                onChange={(e) => setRecordForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">Boeken</button>
              <button type="button" className="btn-secondary" onClick={() => setRecordingPayment(null)}>Annuleren</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminPayments;
